import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";
import { scrollToTop } from "../formHelpers";
import { getAttorneySurfaceConfig } from "../attorneyInformationConfig";
import { useAttorneyInformationCore } from "./useAttorneyInformationCore";

function getLawFirmFetchDeps(isHistoric, refetchOnAttorneyInvolvedChange, attorneyInvolved, isGuest) {
  if (isHistoric) {
    return refetchOnAttorneyInvolvedChange ? [attorneyInvolved] : [];
  }
  return [isGuest];
}

/** Dependency list for the law-firm sync effect (avoids nested ternaries in useEffect). */
export function getAttorneyLawFirmSyncDeps(
  cfg,
  { lawFirm, lawFirmAddress, attorneyOfRecord, isGuest }
) {
  if (cfg.useDebouncedAttorneySearch) {
    return [lawFirm, isGuest];
  }
  if (cfg.isHistoric) {
    return [lawFirm, lawFirmAddress, attorneyOfRecord];
  }
  return [lawFirm, lawFirmAddress, attorneyOfRecord, isGuest];
}

/**
 * Shared law-firm / attorney-of-record step logic for bond creation wizards (Phase 14 / 9.10B).
 */
export function useAttorneyInformation({
  cfg,
  data,
  updateData,
  isGuest,
  globalData,
}) {
  const surface = cfg.isHistoric ? "historic" : "creation";
  const surfaceCfg = getAttorneySurfaceConfig(surface);
  const initialInvolved = data.attorneyInvolved || cfg.attorneyInvolvedDefault;

  const [attorneyInvolved, setAttorneyInvolved] = useState(initialInvolved);
  const attorneyInvolvedValue = cfg.attorneyInvolvedMutable
    ? attorneyInvolved
    : data.attorneyInvolved || cfg.attorneyInvolvedDefault;

  const [lawFirm, setLawFirm] = useState(data.lawFirm || null);
  const [lawFirmAddress, setLawFirmAddress] = useState(data.lawFirmAddress || null);
  const [attorneyOfRecord, setAttorneyOfRecord] = useState(data.attorneyOfRecord || null);

  const [fuzzyMatchLocationDialogOpen, setFuzzyMatchLocationDialogOpen] = useState(false);
  const [fuzzyMatchLocationResult, setFuzzyMatchLocationResult] = useState(null);

  const { role } = useSelector((state) => state.auth);
  const lawfirm = useSelector((state) => state.lawfirm);
  const pgId = useSelector((state) => state.pg.pgId);

  const core = useAttorneyInformationCore({
    surface: surfaceCfg.surface,
    cfg,
    data,
    updateData,
  });

  const {
    lawFirms,
    setLawFirmLocations,
    attorneys,
    setAttorneys,
    loadingLocations,
    setLoadingLocations,
    newAddress,
    fetchLawFirms: coreFetchLawFirms,
    fetchLocationsByLawFirm: coreFetchLocations,
    fetchAttorneysByLawFirm: coreFetchAttorneys,
    postNewLocation,
    handleCloseAddressDialog,
    handleAddAddress: coreHandleAddAddress,
    attorneySearchInput,
    handleAttorneySearchInputChange,
    clearAttorneySearch,
    ...coreRest
  } = core;

  useEffect(() => {
    if (
      (cfg.pgAutoSetToNoHistoric && !role && pgId) ||
      (cfg.pgAutoSetToNo && role === "professional guardian" && pgId)
    ) {
      setAttorneyInvolved("No");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cfg.attorneyInvolvedMutable) return;
    updateData({ ...data, attorneyInvolved: attorneyInvolved });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attorneyInvolved]);

  const buildGuestPayload = () => {
    const base = {
      ...data,
      lawFirm: null,
      lawFirmAddress: null,
      attorneyOfRecord: null,
      attorneyInvolved: cfg.guestAttorneyInvolved,
    };
    if (cfg.guestFieldsFromGlobalData && globalData?.attorneyInformation) {
      return {
        ...base,
        guest_lawfirm_name: globalData.attorneyInformation.guest_lawfirm_name || "",
        guest_lawfirmaddress_name:
          globalData.attorneyInformation.guest_lawfirmaddress_name || "",
        guest_attorney_name: globalData.attorneyInformation.guest_attorney_name || "",
      };
    }
    return {
      ...base,
      guest_lawfirm_name: data.guest_lawfirm_name || "",
      guest_lawfirmaddress_name: data.guest_lawfirmaddress_name || "",
      guest_attorney_name: data.guest_attorney_name || "",
    };
  };

  const fetchLocationsByLawFirm = async (lawFirmId) => {
    await coreFetchLocations(lawFirmId, {
      onLoaded: (formattedLocations) => {
        if (data.lawFirmAddress) {
          return;
        }
        if (formattedLocations.length === 1) {
          setLawFirmAddress(formattedLocations[0].value);
        } else if (formattedLocations.length > 1) {
          const mainLocation = formattedLocations.find(
            (location) => location.label === "Main Location"
          );
          if (mainLocation) {
            setLawFirmAddress(mainLocation.value);
          }
        }
      },
    });
  };

  const fetchAttorneysByLawFirm = async (lawFirmId) => {
    await coreFetchAttorneys(lawFirmId, {
      onLoaded: (filteredAttorneys) => {
        if (data.attorneyOfRecord) {
          return;
        }
        if (filteredAttorneys.length === 1) {
          setAttorneyOfRecord(filteredAttorneys[0].value);
        } else if (filteredAttorneys.length > 1) {
          const defaultAttorney = filteredAttorneys.find((attorney) => attorney.is_default);
          if (defaultAttorney) {
            setAttorneyOfRecord(defaultAttorney.value);
          }
        }
      },
    });
  };

  useEffect(() => {
    if (!cfg.isHistoric && isGuest) {
      updateData(buildGuestPayload());
      return;
    }
    if (cfg.useDebouncedAttorneySearch) {
      if (!lawFirm) {
        return;
      }
      void fetchLocationsByLawFirm(lawFirm);
      return;
    }
    void fetchLocationsByLawFirm(lawFirm);
    void fetchAttorneysByLawFirm(lawFirm);
    updateData({
      ...data,
      lawFirm,
      lawFirmAddress,
      attorneyOfRecord,
      attorneyInvolved: attorneyInvolvedValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, getAttorneyLawFirmSyncDeps(cfg, {
    lawFirm,
    lawFirmAddress,
    attorneyOfRecord,
    isGuest,
  }));

  const fetchLawFirms = async () => {
    await coreFetchLawFirms(() => {
      if (role === "law_firm_user" || role === "law_firm_admin") {
        if (cfg.lawFirmUserSetsAttorneyYes) {
          setAttorneyInvolved("Yes");
        }
        setLawFirm(lawfirm.lawfirmId);
        setLawFirmAddress(lawfirm.locationId);
        setAttorneyOfRecord(lawfirm.lawfirmUserId);
      }
    });
  };

  useEffect(() => {
    if (cfg.isHistoric) {
      void fetchLawFirms();
      scrollToTop();
      return;
    }
    if (isGuest) {
      scrollToTop();
      return;
    }
    void fetchLawFirms();
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, getLawFirmFetchDeps(
    cfg.isHistoric,
    cfg.refetchLawFirmsOnAttorneyInvolvedChange,
    attorneyInvolved,
    isGuest
  ));

  const handleLawFirmChange = (event, newValue) => {
    if (newValue) {
      setLawFirm(newValue.value);
      void fetchLocationsByLawFirm(newValue.value);
      if (cfg.useDebouncedAttorneySearch) {
        clearAttorneySearch();
        updateData({
          ...data,
          lawFirm: newValue.value,
          lawFirmAddress: null,
          attorneyOfRecord: null,
          attorneyInvolved: attorneyInvolvedValue,
        });
        return;
      }
      void fetchAttorneysByLawFirm(newValue.value);
      updateData({
        ...data,
        lawFirm,
        lawFirmAddress,
        attorneyOfRecord,
        attorneyInvolved: attorneyInvolvedValue,
      });
    } else {
      updateData({
        ...data,
        lawFirm: null,
        lawFirmAddress: null,
        attorneyOfRecord: null,
      });
      setLawFirmLocations([]);
      if (cfg.useDebouncedAttorneySearch) {
        clearAttorneySearch();
      } else {
        setAttorneys([]);
      }
    }
  };

  const handleAddAddress = async () => {
    if (!surfaceCfg.fuzzyMatchLocation) {
      await coreHandleAddAddress();
      return;
    }
    try {
      setLoadingLocations(true);
      const fuzzyRes = await apiClient("/fuzzy-match-lawfirm-location", "POST", {
        lawfirm_id: data.lawFirm,
        address: newAddress.address,
        city: newAddress.city,
        state: newAddress.state,
        zip_code: newAddress.zip,
        county: newAddress.county || "",
        country: "USA",
      });
      const matchedId = fuzzyRes?.match?.law_firm_location_id;
      const hasMatch = fuzzyRes?.match != null && matchedId != null;
      if (hasMatch) {
        setFuzzyMatchLocationResult({
          law_firm_location_id: matchedId,
          score: fuzzyRes?.score ?? 0,
        });
        setFuzzyMatchLocationDialogOpen(true);
        setLoadingLocations(false);
        return;
      }
      await postNewLocation();
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("Failed to add address!");
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleUseExistingLocation = () => {
    if (!fuzzyMatchLocationResult) return;
    updateData({
      ...data,
      lawFirmAddress: fuzzyMatchLocationResult.law_firm_location_id,
    });
    void fetchLocationsByLawFirm(data.lawFirm);
    handleCloseAddressDialog();
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    toast.success("Using existing location.");
  };

  const handleCreateNewLocationAnyway = async () => {
    setFuzzyMatchLocationDialogOpen(false);
    setFuzzyMatchLocationResult(null);
    try {
      setLoadingLocations(true);
      await postNewLocation();
    } catch (error) {
      console.error("Error adding address:", error);
      toast.error("Failed to add address!");
    } finally {
      setLoadingLocations(false);
    }
  };

  const showGuestForm = () => {
    if (cfg.guestFormWhenGuestOnly && isGuest) return true;
    if (cfg.guestFormRequiresAttorneyYes && isGuest && attorneyInvolvedValue === "Yes") {
      return true;
    }
    return false;
  };

  const showRegisteredForm = () => {
    if (showGuestForm()) return false;
    return attorneyInvolvedValue === "Yes";
  };

  const showAttorneyToggle = () => {
    if (!cfg.showAttorneyInvolvedToggle) return false;
    if (cfg.showToggleOnlyWhenNotGuest && isGuest) return false;
    return true;
  };

  const radioGroupValue = cfg.radioValueFromData
    ? data.attorneyInvolved || cfg.attorneyInvolvedDefault
    : attorneyInvolved;

  return {
    cfg,
    data,
    updateData,
    isGuest,
    attorneyInvolved,
    setAttorneyInvolved,
    attorneyInvolvedValue,
    lawFirms,
    lawFirmLocations: core.lawFirmLocations,
    attorneys,
    loadingLocations,
    newAddress,
    fuzzyMatchLocationDialogOpen,
    setFuzzyMatchLocationDialogOpen,
    fuzzyMatchLocationResult,
    setFuzzyMatchLocationResult,
    handleLawFirmChange,
    handleAddAddress,
    handleUseExistingLocation,
    handleCreateNewLocationAnyway,
    showGuestForm,
    showRegisteredForm,
    showAttorneyToggle,
    radioGroupValue,
    attorneySearchInput,
    handleAttorneySearchInputChange,
    ...coreRest,
  };
}
